import {
  isValidDate,
  checkBirthDateValidity,
  isEmptyDate,
} from "./dateHelpers";
import states from "../pages/EnrollmentFormPage/states";
import {
  EnrollPhoneNumber,
  EnrollData,
  PlanInfoNeededForEnrollment,
  PlanSNPType,
  PlanType,
  PlanCategoryType,
  AppState,
  MCTDate,
  EnrollDataForAPI,
  Plan,
  EnrollData2021,
  EnrollData2021ForAPI,
} from "../@types";
import { isEmptySSN, isValidSsn, SSN } from "../components/SSNInput";
import { isMapdPlanType, isPdPlanType, isValidEmail } from ".";

const getStateAbbrFromFipsCode = (stateCode: string): string => {
  const state = states.find(s => s.fipsCode === stateCode);
  return state ? state.abbreviation : "";
};

export const removeExtraWhiteSpace = (str: string): string => {
  return str.replace(/\s+/g, " ").trim();
};

const formatDate = (date: MCTDate): string => {
  return date.year && date.month && date.day
    ? `${date.year}-${date.month}-${date.day}`
    : "";
};

const formatAddress = (
  addressLine1: string,
  city: string,
  state: string,
  zip: string
): string => {
  return [addressLine1, city, getStateAbbrFromFipsCode(state), zip].join(", ");
};

export const formatPhone = (phone: EnrollPhoneNumber): string => {
  const { areaCode, exchangeCode, lineNumber } = phone;

  return areaCode && exchangeCode && lineNumber
    ? `${phone.areaCode}-${phone.exchangeCode}-${phone.lineNumber}`
    : "";
};

const formatSSN = (ssn: SSN): string => {
  return ssn.areaNumber && ssn.groupNumber && ssn.serialNumber
    ? `${ssn.areaNumber}-${ssn.groupNumber}-${ssn.serialNumber}`
    : "";
};

export const isCostPlanWithDrugs = (plan: Plan): boolean =>
  plan.plan_type !== PlanType.MA &&
  plan.category === PlanCategoryType.COST_1876;

export const isCostPlanWithoutDrugs = (plan: Plan): boolean =>
  plan.plan_type === PlanType.MA &&
  plan.category === PlanCategoryType.COST_1876;

export const isPffsMa = (plan: Plan): boolean =>
  plan.plan_type === PlanType.MA && plan.category === PlanCategoryType.PFFS;

export const isPffsWithDrugs = (plan: Plan): boolean =>
  plan.plan_type !== PlanType.MA && plan.category === PlanCategoryType.PFFS;

export const shouldShowDrugCoverageQuestion = (plan: Plan): boolean => {
  const isMAPD = isMapdPlanType(plan.plan_type);
  const isPDP = isPdPlanType(plan.plan_type);
  const isCP_PD = isCostPlanWithDrugs(plan);
  const isPFFS_PD = isPffsWithDrugs(plan);
  const isSNP_DE = plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE;

  return isMAPD || isPDP || isSNP_DE || isPFFS_PD || isCP_PD;
};

export const serializePayload = (
  enrollData: EnrollData,
  state: AppState
): EnrollDataForAPI => {
  const { selectedPlan } = state;

  const sepReasonCodes = enrollData.sepReasons.map(r => {
    const { code, date } = r;

    return {
      code: `SEP_REASON_CODE_${code}`,
      date: date ? formatDate(date) : "",
    };
  });

  return {
    plan_year: selectedPlan ? selectedPlan.contract_year : "",
    contract_id: selectedPlan ? selectedPlan.contract_id : "",
    plan_id: selectedPlan ? selectedPlan.plan_id : "",
    segment_id: selectedPlan ? selectedPlan.segment_id : "",
    applicant_info: {
      hicn: enrollData.medicareNumber,
      ssn: formatSSN(enrollData.ssn),
      title: enrollData.title,
      first_name: enrollData.firstName,
      middle_initial: enrollData.middleInitial,
      last_name: enrollData.lastName,
      birthdate: formatDate(enrollData.dob),
      gender: enrollData.sex,
      home_address: {
        address_1: enrollData.address1,
        address_2: enrollData.address2,
        city: enrollData.city,
        state: getStateAbbrFromFipsCode(enrollData.state),
        zipcode: enrollData.zip,
        county: enrollData.county ? enrollData.county.name : "",
      },
      mail_address: {
        address_1: enrollData.mailingAddress1,
        address_2: enrollData.mailingAddress2,
        city: enrollData.mailingCity,
        state: getStateAbbrFromFipsCode(enrollData.mailingState),
        zipcode: enrollData.mailingZip,
      },
      phone: formatPhone(enrollData.primaryPhone),
      email_address: enrollData.email,
      part_a_start: formatDate(enrollData.partAStart),
      part_b_start: formatDate(enrollData.partBStart),
      emergency_contact_name: enrollData.emergencyContactName,
      emergency_contact_phone: formatPhone(enrollData.emergencyContactPhone),
      emergency_contact_relation: enrollData.emergencyContactRelationship,
    },
    other_coverage: enrollData.hasAdditionalDrugCoverage
      ? {
          name: enrollData.additionalDrugCoverage.name,
          id: enrollData.additionalDrugCoverage.id,
          group: enrollData.additionalDrugCoverage.group,
        }
      : null,
    long_term: enrollData.inLongTermCareFacility
      ? {
          name: enrollData.longTermCareFacility.name,
          address: formatAddress(
            enrollData.longTermCareFacility.addressLine1,
            enrollData.longTermCareFacility.city,
            enrollData.longTermCareFacility.state,
            enrollData.longTermCareFacility.zip
          ),
          phone: formatPhone(enrollData.longTermCareFacility.phone),
        }
      : null,
    authorized_rep: enrollData.isEnrollee
      ? null
      : {
          name: enrollData.authorizedRepresentative.name,
          address: {
            address_1: enrollData.authorizedRepresentative.addressLine1,
            address_2: "",
            city: enrollData.authorizedRepresentative.city,
            state: getStateAbbrFromFipsCode(
              enrollData.authorizedRepresentative.state
            ),
            zipcode: enrollData.authorizedRepresentative.zip,
          },
          phone: formatPhone(enrollData.authorizedRepresentative.phone),
          relation: enrollData.authorizedRepresentative.relationship,
        },
    esrd: !!enrollData.renalDisease,
    state_medicaid: !!enrollData.enrolledInMedicaid,
    work_status: !!enrollData.employerHealthCoverage,
    primary_care_physician: enrollData.primaryCareName,
    agent_id: state.csr ? state.csr.id : null,
    lis_level: state.lis,
    part_d_optout: false, //?????
    sep_reason_codes: sepReasonCodes,
    sep_cms_reason_code: enrollData.sepOtherReason,
    premium_direct_pay:
      enrollData.premiumDirectPay === undefined
        ? true
        : enrollData.premiumDirectPay,
    premium_withhold: enrollData.premiumWithhold,
  };
};

export const serializePayload2021 = (
  enrollData: EnrollData2021,
  state: AppState
): EnrollData2021ForAPI => {
  const { selectedPlan } = state;

  const sepReasonCodes = enrollData.sepReasons.map(r => {
    const { code, date } = r;

    return {
      code: `SEP_REASON_CODE_${code}`,
      date: date ? formatDate(date) : "",
    };
  });

  return {
    plan_year: selectedPlan ? selectedPlan.contract_year : "",
    contract_id: selectedPlan ? selectedPlan.contract_id : "",
    plan_id: selectedPlan ? selectedPlan.plan_id : "",
    segment_id: selectedPlan ? selectedPlan.segment_id : "",
    applicant_info: {
      hicn: enrollData.medicareNumber,
      ssn: formatSSN(enrollData.ssn),
      first_name: enrollData.firstName,
      middle_initial: enrollData.middleInitial,
      last_name: enrollData.lastName,
      birthdate: formatDate(enrollData.dob),
      gender: enrollData.sex,
      home_address: {
        address_1: enrollData.address1,
        address_2: enrollData.address2,
        city: enrollData.city,
        state: getStateAbbrFromFipsCode(enrollData.state),
        zipcode: enrollData.zip,
        county: enrollData.county ? enrollData.county.name : "",
      },
      mail_address: {
        address_1: enrollData.mailingAddress1,
        address_2: enrollData.mailingAddress2,
        city: enrollData.mailingCity,
        state: getStateAbbrFromFipsCode(enrollData.mailingState),
        zipcode: enrollData.mailingZip,
      },
      phone: formatPhone(enrollData.primaryPhone),
      email_address: enrollData.email,
      language: enrollData.language || null,
    },
    other_coverage: enrollData.hasAdditionalDrugCoverage
      ? {
          name: enrollData.additionalDrugCoverage.name,
          id: enrollData.additionalDrugCoverage.id,
          group: enrollData.additionalDrugCoverage.group,
        }
      : null,
    authorized_rep: enrollData.isEnrollee
      ? null
      : {
          name: enrollData.authorizedRepresentative.name,
          address: {
            address_1: enrollData.authorizedRepresentative.addressLine1,
            address_2: "",
            city: enrollData.authorizedRepresentative.city,
            state: getStateAbbrFromFipsCode(
              enrollData.authorizedRepresentative.state
            ),
            zipcode: enrollData.authorizedRepresentative.zip,
          },
          phone: formatPhone(enrollData.authorizedRepresentative.phone),
          relation: enrollData.authorizedRepresentative.relationship,
        },
    accessibility_format: enrollData.accessibilityFormat || null,
    email_opt_in: !!enrollData.emailOptIn,
    work_status:
      enrollData.beneWorks === undefined ? null : enrollData.beneWorks,
    spouse_work_status:
      enrollData.spouseWorks === undefined ? null : enrollData.spouseWorks,
    primary_care_physician: enrollData.primaryCareName,
    agent_id: state.csr ? state.csr.id : null,
    lis_level: state.lis,
    sep_reason_codes: sepReasonCodes,
    sep_cms_reason_code: enrollData.sepOtherReason,
    premium_direct_pay:
      enrollData.premiumDirectPay === undefined
        ? true
        : enrollData.premiumDirectPay,
    premium_withhold: enrollData.premiumWithhold,
  };
};

export enum EnrollmentPages {
  Sep = "Sep",
  MedicareInfo = "MedicareInfo",
  PersonalInfo = "PersonalInfo",
  Address = "Address",
  GeneralQuestions = "GeneralQuestions",
  Paying = "Paying",
  Agreement = "Agreement",
  TellUsMore = "TellUsMore",
  Review = "Review",
  Enrolled = "Enrolled",
}

export interface Validity {
  isValid: boolean;
  medicareNumberValidity?: boolean;
  ssnValidity?: boolean;
  partAStartValidity?: boolean;
  partBStartValidity?: boolean;
  understoodCoverageLossWarningValidity?: boolean;
  sexValidity?: boolean;
  firstNameValidity?: boolean;
  middleInitialValidity?: boolean;
  lastNameValidity?: boolean;
  titleValidity?: boolean;
  dobValidity?: boolean;
  phoneValidity?: boolean;
  emailValidity?: boolean;
  addressValidity?: boolean;
  cityValidity?: boolean;
  stateValidity?: boolean;
  zipValidity?: boolean;
  countyValidity?: boolean;
  mailingAddressValidity?: boolean;
  mailingCityValidity?: boolean;
  mailingStateValidity?: boolean;
  mailingZipValidity?: boolean;
  renalDiseaseQuestionValidity?: boolean;
  hasAdditionalDrugCoverageValidity?: boolean;
  additionalDrugCoverageNameValidity?: boolean;
  additionalDrugCoverageIdValidity?: boolean;
  additionalDrugCoverageGroupValidity?: boolean;
  longTermCareNameValidity?: boolean;
  longTermCareAddressLine1Validity?: boolean;
  longTermCareCityValidity?: boolean;
  longTermCareStateValidity?: boolean;
  longTermCareZipValidity?: boolean;
  longTermCarePhoneValidity?: boolean;
  enrolledInMedicaidQuestionValidity?: boolean;
  agreeToContractValidity?: boolean;
  agreeToInformationReleaseValidity?: boolean;
  isEnrolleeValidity?: boolean;
  authorizedRepresentativeNameValidity?: boolean;
  authorizedRepresentativeRelationshipValidity?: boolean;
  authorizedRepresentativeAddressLine1Validity?: boolean;
  authorizedRepresentativeCityValidity?: boolean;
  authorizedRepresentativeStateValidity?: boolean;
  authorizedRepresentativeZipValidity?: boolean;
  authorizedRepresentativePhoneValidity?: boolean;
  readAndUnderstoodContentsValidity?: boolean;
  sepOtherReasonValidity?: boolean;
  sepReasonsValidity?: boolean;
}

export const mbiRegex = /^\d[A-Z]\w\d[A-Z]\w\d[A-Z]{2}\d{2}$/g;

export const isValidMedicareNumber = (
  medicareNumber: string | undefined
): boolean => {
  if (!medicareNumber) return false;

  const isMbi = !!medicareNumber.match(mbiRegex);

  return isMbi;
};

const isValidZip = (zip: string): boolean => {
  return !!zip.match(/^\d{5}$/);
};

export const getMedicareInfoValidity = (
  enrollData: EnrollData,
  plan: PlanInfoNeededForEnrollment
): Validity => {
  const medicareNumberValidity = isValidMedicareNumber(
    enrollData.medicareNumber
  );
  const ssnValidity =
    plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE
      ? isValidSsn(enrollData.ssn)
      : true;
  const partAStartValidity = isValidDate(enrollData.partAStart);
  const partBStartValidity = isValidDate(enrollData.partBStart);
  const understoodCoverageLossWarningValidity = !!enrollData.understoodCoverageLossWarning;

  return {
    isValid:
      medicareNumberValidity &&
      ssnValidity &&
      ((partAStartValidity && partBStartValidity) ||
        (partAStartValidity && isEmptyDate(enrollData.partBStart)) ||
        (isEmptyDate(enrollData.partAStart) && partBStartValidity)) &&
      understoodCoverageLossWarningValidity,
    medicareNumberValidity,
    ssnValidity,
    partAStartValidity,
    partBStartValidity,
    understoodCoverageLossWarningValidity,
  };
};

export const getMedicareInfoValidity2021 = (
  enrollData: EnrollData2021,
  plan: PlanInfoNeededForEnrollment
): Validity => {
  const medicareNumberValidity = isValidMedicareNumber(
    enrollData.medicareNumber
  );
  const ssnValidity =
    plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE
      ? isEmptySSN(enrollData.ssn) || isValidSsn(enrollData.ssn)
      : true;
  const understoodCoverageLossWarningValidity = !!enrollData.understoodCoverageLossWarning;

  return {
    isValid:
      medicareNumberValidity &&
      ssnValidity &&
      understoodCoverageLossWarningValidity,
    medicareNumberValidity,
    ssnValidity,
    understoodCoverageLossWarningValidity,
  };
};

export const getPersonalInfoValidity = (enrollData: EnrollData): Validity => {
  const sexValidity = !!enrollData.sex.trim();
  const firstNameValidity = !!enrollData.firstName.trim();
  const middleInitialValidity =
    enrollData.middleInitial === "" ||
    !!enrollData.middleInitial.trim().match(/^[a-z|A-Z]$/);
  const lastNameValidity = !!enrollData.lastName.trim();
  const titleValidity = !!enrollData.title;
  const dobValidity = checkBirthDateValidity(enrollData.dob);
  const phoneValidity =
    enrollData.primaryPhone.areaCode.length === 3 &&
    enrollData.primaryPhone.exchangeCode.length === 3 &&
    enrollData.primaryPhone.lineNumber.length === 4;
  const emailValidity = enrollData.email
    ? isValidEmail(enrollData.email)
    : true;

  return {
    isValid:
      sexValidity &&
      firstNameValidity &&
      middleInitialValidity &&
      lastNameValidity &&
      titleValidity &&
      dobValidity &&
      phoneValidity &&
      emailValidity,
    sexValidity,
    firstNameValidity,
    middleInitialValidity,
    lastNameValidity,
    titleValidity,
    dobValidity,
    phoneValidity,
    emailValidity,
  };
};

export const getPersonalInfoValidity2021 = (
  enrollData: EnrollData2021
): Validity => {
  const sexValidity = !!enrollData.sex.trim();
  const firstNameValidity = !!enrollData.firstName.trim();
  const middleInitialValidity =
    enrollData.middleInitial === "" ||
    !!enrollData.middleInitial.trim().match(/^[a-z|A-Z]$/);
  const lastNameValidity = !!enrollData.lastName.trim();
  const dobValidity = checkBirthDateValidity(enrollData.dob);
  const phoneValidity =
    enrollData.primaryPhone.areaCode.length === 3 &&
    enrollData.primaryPhone.exchangeCode.length === 3 &&
    enrollData.primaryPhone.lineNumber.length === 4;

  return {
    isValid:
      sexValidity &&
      firstNameValidity &&
      middleInitialValidity &&
      lastNameValidity &&
      dobValidity &&
      phoneValidity,
    sexValidity,
    firstNameValidity,
    middleInitialValidity,
    lastNameValidity,
    dobValidity,
    phoneValidity,
  };
};

const getAddressValidity = (
  enrollData: EnrollData | EnrollData2021
): Validity => {
  const addressValidity = !!enrollData.address1.trim();
  const cityValidity = !!enrollData.city.trim();
  const stateValidity = !!enrollData.state;
  const zipValidity = !!enrollData.zip.trim() && isValidZip(enrollData.zip);
  const countyValidity = !!enrollData.county;

  let mailingAddressValidity = true;
  let mailingCityValidity = true;
  let mailingStateValidity = true;
  let mailingZipValidity = true;
  if (enrollData.differentMailingAddress) {
    mailingAddressValidity = !!enrollData.mailingAddress1.trim();
    mailingCityValidity = !!enrollData.mailingCity.trim();
    mailingStateValidity = !!enrollData.mailingState;
    mailingZipValidity =
      !!enrollData.mailingZip.trim() && isValidZip(enrollData.mailingZip);
  }

  return {
    isValid:
      addressValidity &&
      cityValidity &&
      stateValidity &&
      zipValidity &&
      countyValidity &&
      mailingAddressValidity &&
      mailingCityValidity &&
      mailingStateValidity &&
      mailingZipValidity,
    addressValidity,
    cityValidity,
    stateValidity,
    zipValidity,
    countyValidity,
    mailingAddressValidity,
    mailingCityValidity,
    mailingStateValidity,
    mailingZipValidity,
  };
};

const getGeneralQuestionsValidity = (
  enrollData: EnrollData,
  plan: PlanInfoNeededForEnrollment
): Validity => {
  const isPDP = plan.plan_type === PlanType.PDP;
  const isCP_PD =
    plan.plan_type !== PlanType.MA &&
    plan.category === PlanCategoryType.COST_1876;
  const isPFFS_PD =
    plan.plan_type !== PlanType.MA && plan.category === PlanCategoryType.PFFS;

  const renalDiseaseQuestionValidity =
    isPDP && !isCP_PD && !isPFFS_PD
      ? true
      : enrollData.renalDisease !== undefined;
  const additionalDrugCoverageNameValidity = enrollData.hasAdditionalDrugCoverage
    ? !!enrollData.additionalDrugCoverage.name
    : true;
  const additionalDrugCoverageIdValidity = enrollData.hasAdditionalDrugCoverage
    ? !!enrollData.additionalDrugCoverage.id
    : true;
  const additionalDrugCoverageGroupValidity = enrollData.hasAdditionalDrugCoverage
    ? !!enrollData.additionalDrugCoverage.group
    : true;
  const longTermCareNameValidity = enrollData.inLongTermCareFacility
    ? !!enrollData.longTermCareFacility.name
    : true;
  const longTermCareAddressLine1Validity = enrollData.inLongTermCareFacility
    ? !!enrollData.longTermCareFacility.addressLine1
    : true;
  const longTermCareCityValidity = enrollData.inLongTermCareFacility
    ? !!enrollData.longTermCareFacility.city
    : true;
  const longTermCareStateValidity = enrollData.inLongTermCareFacility
    ? !!enrollData.longTermCareFacility.state
    : true;
  const longTermCareZipValidity = enrollData.inLongTermCareFacility
    ? !!enrollData.longTermCareFacility.zip &&
      isValidZip(enrollData.longTermCareFacility.zip)
    : true;
  const longTermCarePhoneValidity = enrollData.inLongTermCareFacility
    ? !!enrollData.longTermCareFacility.phone &&
      enrollData.longTermCareFacility.phone.areaCode.length === 3 &&
      enrollData.longTermCareFacility.phone.exchangeCode.length === 3 &&
      enrollData.longTermCareFacility.phone.lineNumber.length === 4
    : true;
  const enrolledInMedicaidQuestionValidity =
    isPDP && !isCP_PD && !isPFFS_PD
      ? true
      : enrollData.enrolledInMedicaid !== undefined;

  return {
    isValid:
      renalDiseaseQuestionValidity &&
      additionalDrugCoverageNameValidity &&
      additionalDrugCoverageIdValidity &&
      additionalDrugCoverageGroupValidity &&
      longTermCareNameValidity &&
      longTermCareAddressLine1Validity &&
      longTermCareCityValidity &&
      longTermCareStateValidity &&
      longTermCareZipValidity &&
      longTermCarePhoneValidity &&
      enrolledInMedicaidQuestionValidity,
    renalDiseaseQuestionValidity,
    additionalDrugCoverageNameValidity,
    additionalDrugCoverageIdValidity,
    additionalDrugCoverageGroupValidity,
    longTermCareNameValidity,
    longTermCareAddressLine1Validity,
    longTermCareCityValidity,
    longTermCareStateValidity,
    longTermCareZipValidity,
    longTermCarePhoneValidity,
    enrolledInMedicaidQuestionValidity,
  };
};

const getGeneralQuestionsValidity2021 = (
  enrollData: EnrollData2021,
  plan: Plan
): Validity => {
  const hasAdditionalDrugCoverageValidity = shouldShowDrugCoverageQuestion(plan)
    ? enrollData.hasAdditionalDrugCoverage !== undefined
    : true;
  const additionalDrugCoverageNameValidity = enrollData.hasAdditionalDrugCoverage
    ? !!enrollData.additionalDrugCoverage.name
    : true;
  const additionalDrugCoverageIdValidity = enrollData.hasAdditionalDrugCoverage
    ? !!enrollData.additionalDrugCoverage.id
    : true;
  const additionalDrugCoverageGroupValidity = enrollData.hasAdditionalDrugCoverage
    ? !!enrollData.additionalDrugCoverage.group
    : true;
  const emailValidity = enrollData.emailOptIn
    ? isValidEmail(enrollData.email)
    : true;

  return {
    isValid:
      hasAdditionalDrugCoverageValidity &&
      additionalDrugCoverageNameValidity &&
      additionalDrugCoverageIdValidity &&
      additionalDrugCoverageGroupValidity &&
      emailValidity,
    additionalDrugCoverageNameValidity,
    additionalDrugCoverageIdValidity,
    additionalDrugCoverageGroupValidity,
    emailValidity,
    hasAdditionalDrugCoverageValidity,
  };
};

const getAgreementValidity = (enrollData: EnrollData): Validity => {
  const agreeToContractValidity = !!enrollData.agreeToContract;
  const agreeToInformationReleaseValidity = !!enrollData.agreeToInformationRelease;
  const isEnrolleeValidity = enrollData.isEnrollee !== undefined;
  const authorizedRepresentativeNameValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.name
    : true;
  const authorizedRepresentativeRelationshipValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.relationship
    : true;
  const authorizedRepresentativeAddressLine1Validity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.addressLine1
    : true;
  const authorizedRepresentativeCityValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.city
    : true;
  const authorizedRepresentativeStateValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.state
    : true;
  const authorizedRepresentativeZipValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.zip &&
      isValidZip(enrollData.authorizedRepresentative.zip)
    : true;
  const authorizedRepresentativePhoneValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.phone &&
      enrollData.authorizedRepresentative.phone.areaCode.length === 3 &&
      enrollData.authorizedRepresentative.phone.exchangeCode.length === 3 &&
      enrollData.authorizedRepresentative.phone.lineNumber.length === 4
    : true;

  return {
    isValid:
      agreeToContractValidity &&
      agreeToInformationReleaseValidity &&
      isEnrolleeValidity &&
      authorizedRepresentativeNameValidity &&
      authorizedRepresentativeRelationshipValidity &&
      authorizedRepresentativeAddressLine1Validity &&
      authorizedRepresentativeCityValidity &&
      authorizedRepresentativeStateValidity &&
      authorizedRepresentativeZipValidity &&
      authorizedRepresentativePhoneValidity,
    agreeToContractValidity,
    agreeToInformationReleaseValidity,
    isEnrolleeValidity,
    authorizedRepresentativeNameValidity,
    authorizedRepresentativeRelationshipValidity,
    authorizedRepresentativeAddressLine1Validity,
    authorizedRepresentativeCityValidity,
    authorizedRepresentativeStateValidity,
    authorizedRepresentativeZipValidity,
    authorizedRepresentativePhoneValidity,
  };
};

const getAgreementValidity2021 = (enrollData: EnrollData2021): Validity => {
  const agreeToContractValidity = !!enrollData.agreeToContract;
  const isEnrolleeValidity = enrollData.isEnrollee !== undefined;
  const authorizedRepresentativeNameValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.name
    : true;
  const authorizedRepresentativeRelationshipValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.relationship
    : true;
  const authorizedRepresentativeAddressLine1Validity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.addressLine1
    : true;
  const authorizedRepresentativeCityValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.city
    : true;
  const authorizedRepresentativeStateValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.state
    : true;
  const authorizedRepresentativeZipValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.zip &&
      isValidZip(enrollData.authorizedRepresentative.zip)
    : true;
  const authorizedRepresentativePhoneValidity = !enrollData.isEnrollee
    ? !!enrollData.authorizedRepresentative.phone &&
      enrollData.authorizedRepresentative.phone.areaCode.length === 3 &&
      enrollData.authorizedRepresentative.phone.exchangeCode.length === 3 &&
      enrollData.authorizedRepresentative.phone.lineNumber.length === 4
    : true;

  return {
    isValid:
      agreeToContractValidity &&
      isEnrolleeValidity &&
      authorizedRepresentativeNameValidity &&
      authorizedRepresentativeRelationshipValidity &&
      authorizedRepresentativeAddressLine1Validity &&
      authorizedRepresentativeCityValidity &&
      authorizedRepresentativeStateValidity &&
      authorizedRepresentativeZipValidity &&
      authorizedRepresentativePhoneValidity,
    agreeToContractValidity,
    isEnrolleeValidity,
    authorizedRepresentativeNameValidity,
    authorizedRepresentativeRelationshipValidity,
    authorizedRepresentativeAddressLine1Validity,
    authorizedRepresentativeCityValidity,
    authorizedRepresentativeStateValidity,
    authorizedRepresentativeZipValidity,
    authorizedRepresentativePhoneValidity,
  };
};

const getReviewValidity = (
  enrollData: EnrollData | EnrollData2021
): Validity => {
  const readAndUnderstoodContentsValidity =
    enrollData.readAndUnderstoodContents;
  return {
    isValid: !!enrollData.readAndUnderstoodContents,
    readAndUnderstoodContentsValidity,
  };
};

const getSepValidity = (enrollData: EnrollData | EnrollData2021): Validity => {
  const { sepReasons, hasSepOtherReason, sepOtherReason } = enrollData;

  let datesValid = true;
  let otherReasonValid = true;
  let reasonsValid = true;

  if (!sepReasons.length && !sepOtherReason) {
    reasonsValid = false;
  }

  if (hasSepOtherReason && !sepOtherReason) {
    otherReasonValid = false;
  }

  sepReasons.forEach(reason => {
    if (reason.requiresDate && (!reason.date || !isValidDate(reason.date))) {
      datesValid = false;
    }
  });

  return {
    isValid: datesValid && otherReasonValid && reasonsValid,
    sepReasonsValidity: reasonsValid,
    sepOtherReasonValidity: otherReasonValid,
  };
};

export const getValidationObject = (
  enrollData: EnrollData | EnrollData2021,
  plan: PlanInfoNeededForEnrollment,
  page: string
): Validity => {
  if (plan.contract_year === "2021") {
    switch (page) {
      case EnrollmentPages.Sep:
        return getSepValidity(enrollData);
      case EnrollmentPages.MedicareInfo:
        return getMedicareInfoValidity2021(enrollData, plan);
      case EnrollmentPages.PersonalInfo:
        return getPersonalInfoValidity2021(enrollData);
      case EnrollmentPages.Address:
        return getAddressValidity(enrollData);
      case EnrollmentPages.GeneralQuestions:
        return getGeneralQuestionsValidity2021(enrollData, plan as Plan);
      case EnrollmentPages.Agreement:
        return getAgreementValidity2021(enrollData);
      case EnrollmentPages.Review:
        return getReviewValidity(enrollData);
      default:
        return {
          isValid: true,
        };
    }
  }

  switch (page) {
    case EnrollmentPages.Sep:
      return getSepValidity(enrollData);
    case EnrollmentPages.MedicareInfo:
      return getMedicareInfoValidity(enrollData as EnrollData, plan);
    case EnrollmentPages.PersonalInfo:
      return getPersonalInfoValidity(enrollData as EnrollData);
    case EnrollmentPages.Address:
      return getAddressValidity(enrollData);
    case EnrollmentPages.GeneralQuestions:
      return getGeneralQuestionsValidity(enrollData as EnrollData, plan);
    case EnrollmentPages.Agreement:
      return getAgreementValidity(enrollData as EnrollData);
    case EnrollmentPages.Review:
      return getReviewValidity(enrollData as EnrollData);
    default:
      return {
        isValid: true,
      };
  }
};
